import { useState, useEffect } from 'react';
import { aquaApi } from '../aquaApi';
import { fmtDate } from '../dateUtils';
import DateInput from '../DateInput';

const UOMS = ['Kgs', 'Ltrs', 'Gms', 'Nos'];

const EMPTY_HDR = { cropId: '', usageDate: new Date().toISOString().split('T')[0] };

const EMPTY_ROW = { pondNo: '', doc: '', productName: '', quantityUsed: '', uom: 'Kgs', notes: '' };

const CAT_COLOR = {
  'FEED':       { bg: '#e6f4ea', color: '#137333' },
  'PROBIOTIC':  { bg: '#e8f0fe', color: '#1a73e8' },
  'MINERAL':    { bg: '#fce8b2', color: '#b45309' },
  'POND PREP':  { bg: '#fce8e6', color: '#c5221f' },
  'FEED MIX':   { bg: '#f3e8fd', color: '#7b1fa2' },
  'YEAST':      { bg: '#fff3e0', color: '#e65100' },
  'GEL BINDER': { bg: '#e8f5e9', color: '#2e7d32' },
  'ZEOLITE':    { bg: '#fbe9e7', color: '#bf360c' },
  'OTHER':      { bg: '#f1f3f4', color: '#5f6368' },
};

const fmt3 = (v) => v != null && v !== '' ? parseFloat(v).toFixed(3) : '—';

function makeRow() { return { ...EMPTY_ROW }; }

function calcDocFromRelease(pondNo, usageDate, releaseMap) {
  const rd = releaseMap[pondNo];
  if (!rd || !usageDate) return '';
  const diff = Math.floor((new Date(usageDate) - new Date(rd)) / 86400000);
  return diff > 0 ? String(diff) : '';
}

export default function AquaChem({ cropId, cropCode, activeCrops = [], role = 'admin' }) {
  const canWrite  = role !== 'readonly';
  const canDelete = role === 'admin';

  const [rows, setRows]               = useState([]);
  const [products, setProducts]       = useState([]);
  const [farmPonds, setFarmPonds]     = useState([]);
  const [releaseMap, setReleaseMap]   = useState({});

  const [hdr, setHdr]                 = useState({ ...EMPTY_HDR, cropId: cropId || '' });
  const [formRows, setFormRows]       = useState([makeRow()]);
  const [editId, setEditId]           = useState(null);
  const [tab, setTab]                 = useState('list');
  const [filterCrop, setFilterCrop]   = useState(cropId || '');
  const [loading, setLoading]         = useState(true);
  const [err, setErr]                 = useState('');
  const [saving, setSaving]           = useState(false);

  const load = () => {
    setLoading(true);
    aquaApi.getChemUsage(filterCrop || null)
      .then(r => setRows(Array.isArray(r) ? r : []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    aquaApi.getProductNames().then(d => {
      if (Array.isArray(d)) setProducts(
        d.filter(p => p.category?.toUpperCase() === 'CHEMICALS')
         .sort((a, b) => a.name.localeCompare(b.name))
      );
    }).catch(() => {});
    aquaApi.getPonds().then(d => { if (Array.isArray(d)) setFarmPonds(d.slice().sort((a, b) => a.pond_name.localeCompare(b.pond_name))); }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [filterCrop]);
  useEffect(() => { setFilterCrop(cropId || ''); }, [cropId]);
  useEffect(() => { setHdr(h => ({ ...h, cropId: cropId || '' })); }, [cropId]);

  // Load release map when crop changes
  useEffect(() => {
    const cid = hdr.cropId;
    if (!cid) { setReleaseMap({}); return; }
    aquaApi.getCropRelease(cid).then(data => {
      if (Array.isArray(data)) {
        const map = {};
        data.forEach(r => { map[r.pond_no] = r.release_date; });
        setReleaseMap(map);
      }
    }).catch(() => setReleaseMap({}));
  }, [hdr.cropId]);

  // Recalculate DOC for all rows when releaseMap or date changes
  useEffect(() => {
    if (tab !== 'form') return;
    setFormRows(prev => prev.map(row => {
      const computed = calcDocFromRelease(row.pondNo, hdr.usageDate, releaseMap);
      return { ...row, doc: computed || row.doc };
    }));
  }, [releaseMap, hdr.usageDate]);

  const setH = (k, v) => setHdr(h => ({ ...h, [k]: v }));

  const pondOptions = farmPonds.map(p => p.pond_name);

  const updateRow = (idx, k, v) => {
    setFormRows(prev => {
      const next = prev.map((r, i) => i !== idx ? r : { ...r, [k]: v });
      if (k === 'pondNo') {
        const computed = calcDocFromRelease(v, hdr.usageDate, releaseMap);
        next[idx] = { ...next[idx], doc: computed || '' };
      }
      return next;
    });
  };

  const addRow    = () => setFormRows(prev => [...prev, makeRow()]);
  const removeRow = (idx) => setFormRows(prev => prev.filter((_, i) => i !== idx));

  const validRows   = formRows.filter(r => r.productName && parseFloat(r.quantityUsed) > 0);
  const isFormValid = !!(hdr.cropId && hdr.usageDate && (editId ? formRows.length > 0 && formRows[0].productName : validRows.length > 0));

  const handleSave = async () => {
    if (!hdr.cropId)    { setErr('Select a crop'); return; }
    if (!hdr.usageDate) { setErr('Select a date'); return; }
    const toSave = editId ? formRows : validRows;
    if (toSave.length === 0) { setErr('Add at least one row with Product and quantity > 0'); return; }
    setSaving(true); setErr('');
    try {
      for (const row of toSave) {
        const payload = {
          cropId:       hdr.cropId,
          usageDate:    hdr.usageDate,
          pondNo:       row.pondNo || null,
          doc:          row.doc    || null,
          productName:  row.productName,
          uom:          row.uom || 'Kgs',
          quantityUsed: parseFloat(row.quantityUsed) || 0,
          notes:        row.notes || '',
        };
        if (editId) await aquaApi.updateChemUsage(editId, payload);
        else        await aquaApi.createChemUsage(payload);
      }
      setHdr({ ...EMPTY_HDR, cropId: cropId || '' });
      setFormRows([makeRow()]);
      setEditId(null); setTab('list'); load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleEdit = (r) => {
    setHdr({ cropId: String(r.crop_id || ''), usageDate: r.usage_date ? r.usage_date.split('T')[0] : '' });
    setFormRows([{
      pondNo:       r.pond_no       || '',
      doc:          r.doc != null   ? String(r.doc) : '',
      productName:  r.product_name  || '',
      quantityUsed: r.quantity_used != null ? String(r.quantity_used) : '',
      uom:          r.uom           || 'Kgs',
      notes:        r.notes         || '',
    }]);
    setEditId(r.id); setTab('form');
  };

  const showCropCol = !filterCrop;
  const totalQty    = rows.reduce((s, r) => s + parseFloat(r.quantity_used || 0), 0);
  const uniqueProds = new Set(rows.map(r => r.product_name)).size;

  const summaryMap = {};
  rows.forEach(r => {
    const key = `${r.product_name}|||${r.uom}`;
    if (!summaryMap[key]) summaryMap[key] = { product_name: r.product_name, uom: r.uom, category: r.category, total: 0, count: 0 };
    summaryMap[key].total += parseFloat(r.quantity_used || 0);
    summaryMap[key].count += 1;
  });
  const summaryRows = Object.values(summaryMap).sort((a, b) => b.total - a.total);

  const cellSt = { padding: '3px 5px', verticalAlign: 'middle' };
  const inSt   = { padding: '3px 6px', fontSize: 12, height: 28, boxSizing: 'border-box', width: '100%' };

  if (loading) return <div className="loading-msg">Loading chemical usage…</div>;

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'list'   ? ' active' : ''}`} onClick={() => { setTab('list'); setEditId(null); }}>🧪 Chem Usage</button>
        {canWrite && <button className={`tab${tab === 'form' ? ' active' : ''}`} onClick={() => setTab('form')}>{editId ? '✏️ Edit Entry' : '➕ Log Usage'}</button>}
        <button className={`tab${tab === 'report' ? ' active' : ''}`} onClick={() => setTab('report')}>📊 Summary</button>
      </div>

      {/* Crop filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '8px 12px', background: '#f8f9fa', borderRadius: 8, border: '1px solid #e8eaed' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#5f6368' }}>🦐 Crop:</span>
        <select className="form-control" style={{ width: 200, padding: '4px 8px' }}
          value={filterCrop} onChange={e => { setFilterCrop(e.target.value); setHdr(h => ({ ...h, cropId: e.target.value })); }}>
          <option value="">All Crops</option>
          {activeCrops.slice().sort((a, b) => a.crop_code.localeCompare(b.crop_code)).map(c => <option key={c.id} value={c.id}>{c.crop_code}</option>)}
        </select>
        {!filterCrop && <span style={{ fontSize: 12, color: '#1a73e8', fontWeight: 600 }}>Showing all crops</span>}
      </div>

      {/* ── List ── */}
      {tab === 'list' && (
        <div>
          <div className="stats-grid" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="stat-icon blue" style={{ fontSize: 22 }}>🧪</div>
              <div><div className="stat-label">Unique Products</div><div className="stat-value">{uniqueProds}</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon green" style={{ fontSize: 22 }}>📋</div>
              <div><div className="stat-label">Log Entries</div><div className="stat-value">{rows.length}</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon orange" style={{ fontSize: 22 }}>⚖️</div>
              <div><div className="stat-label">Total Qty Used</div><div className="stat-value">{totalQty.toFixed(3)}</div></div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">🧪 Chemical Usage Log {filterCrop && cropCode ? `— ${cropCode}` : ''}</div>
              {canWrite && (
                <button className="btn btn-primary" style={{ padding: '5px 16px', fontSize: 13 }}
                  onClick={() => { setHdr({ ...EMPTY_HDR, cropId: filterCrop || '' }); setFormRows([makeRow()]); setEditId(null); setTab('form'); }}>
                  ➕ Log Usage
                </button>
              )}
            </div>
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    {showCropCol && <th>Crop</th>}
                    <th>Date</th><th>DOC</th><th>Pond</th><th>Product</th><th>Category</th>
                    <th style={{ textAlign: 'right' }}>Qty Used</th><th>UOM</th>
                    <th>Notes</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={showCropCol ? 10 : 9} style={{ textAlign: 'center', color: '#888', padding: 32 }}>
                      No chemical usage entries yet
                    </td></tr>
                  )}
                  {rows.map(r => {
                    const cat = CAT_COLOR[r.category] || CAT_COLOR['OTHER'];
                    return (
                      <tr key={r.id}>
                        {showCropCol && (
                          <td><span style={{ background: '#f3e8fd', color: '#7b1fa2', borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{r.crop_code}</span></td>
                        )}
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.usage_date)}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600, color: '#1a73e8' }}>{r.doc || '—'}</td>
                        <td>{r.pond_no || '—'}</td>
                        <td style={{ fontWeight: 600 }}>{r.product_name}</td>
                        <td><span style={{ background: cat.bg, color: cat.color, borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{r.category || '—'}</span></td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt3(r.quantity_used)}</td>
                        <td>{r.uom}</td>
                        <td style={{ fontSize: 12, color: '#888' }}>{r.notes || '—'}</td>
                        <td>
                          {canWrite && <button className="btn-sm btn-secondary" onClick={() => handleEdit(r)} style={{ marginRight: 4 }}>Edit</button>}
                          {canDelete && <button className="btn-sm btn-danger" onClick={async () => { if (window.confirm('Delete?')) { await aquaApi.deleteChemUsage(r.id); load(); } }}>Del</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {rows.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={showCropCol ? 6 : 5} style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px' }}>TOTAL</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, padding: '10px 12px', color: '#137333' }}>{totalQty.toFixed(3)}</td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Form ── */}
      {tab === 'form' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">{editId ? '✏️ Edit Chemical Usage' : '➕ Log Chemical Usage'}</div>
          </div>
          <div className="card-body">
            {err && <div className="alert alert-error" style={{ marginBottom: 12 }}>{err}</div>}

            {/* Header: Crop | Date */}
            <div className="aq-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Crop <span style={{ color: '#c5221f' }}>*</span></label>
                <select className="form-control" value={hdr.cropId}
                  onChange={e => setH('cropId', e.target.value)} disabled={!!editId}>
                  <option value="">— Select Crop —</option>
                  {activeCrops.slice().sort((a, b) => a.crop_code.localeCompare(b.crop_code)).map(c => <option key={c.id} value={c.id}>{c.crop_code}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Date <span style={{ color: '#c5221f' }}>*</span></label>
                <DateInput className="form-control" value={hdr.usageDate} onChange={e => setH('usageDate', e.target.value)} />
              </div>
            </div>

            {/* Hint */}
            <div style={{ fontSize: 12, color: '#5f6368', marginBottom: 8, padding: '6px 10px', background: '#f8f9fa', borderRadius: 6, border: '1px solid #e8eaed' }}>
              💡 Select <strong>Pond</strong> to auto-fill DOC. Add multiple rows to log usage across ponds in one entry.
            </div>

            {/* Rows table */}
            <div style={{ overflowX: 'auto', marginBottom: 8 }}>
              <table style={{ borderCollapse: 'collapse', minWidth: 800, width: '100%', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f1f3f4' }}>
                    <th style={{ ...cellSt, width: 28, textAlign: 'center' }}>#</th>
                    <th style={{ ...cellSt, width: 120 }}>Pond</th>
                    <th style={{ ...cellSt, width: 56, textAlign: 'center' }}>DOC</th>
                    <th style={{ ...cellSt, minWidth: 180 }}>Product Name <span style={{ color: '#c5221f' }}>*</span></th>
                    <th style={{ ...cellSt, width: 100, textAlign: 'right' }}>Qty Used <span style={{ color: '#c5221f' }}>*</span></th>
                    <th style={{ ...cellSt, width: 80 }}>UOM</th>
                    <th style={{ ...cellSt, minWidth: 160 }}>Notes</th>
                    <th style={{ ...cellSt, width: 28 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {formRows.map((row, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #e8eaed' }}>
                      <td style={{ ...cellSt, textAlign: 'center', color: '#888', fontWeight: 700 }}>{idx + 1}</td>
                      <td style={cellSt}>
                        <select className="form-control" style={inSt}
                          value={row.pondNo} onChange={e => updateRow(idx, 'pondNo', e.target.value)}>
                          <option value="">— Pond —</option>
                          {pondOptions.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </td>
                      <td style={{ ...cellSt, textAlign: 'center' }}>
                        <input className="form-control" style={{ ...inSt, textAlign: 'center',
                          background: row.doc ? 'rgba(26,115,232,.10)' : undefined,
                          color: row.doc ? '#1a73e8' : undefined, fontWeight: row.doc ? 700 : undefined }}
                          type="number" min="1" step="1" placeholder="—"
                          value={row.doc}
                          onChange={e => updateRow(idx, 'doc', e.target.value)} />
                      </td>
                      <td style={cellSt}>
                        <select className="form-control" style={inSt}
                          value={row.productName} onChange={e => updateRow(idx, 'productName', e.target.value)}>
                          <option value="">— Select Product —</option>
                          {products.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                        </select>
                      </td>
                      <td style={cellSt}>
                        <input className="form-control" style={{ ...inSt, textAlign: 'right' }}
                          type="number" step="0.001" min="0" placeholder="0.000"
                          value={row.quantityUsed}
                          onChange={e => updateRow(idx, 'quantityUsed', e.target.value)} />
                      </td>
                      <td style={cellSt}>
                        <select className="form-control" style={inSt}
                          value={row.uom} onChange={e => updateRow(idx, 'uom', e.target.value)}>
                          {UOMS.map(u => <option key={u}>{u}</option>)}
                        </select>
                      </td>
                      <td style={cellSt}>
                        <input className="form-control" style={inSt}
                          placeholder="Optional notes"
                          value={row.notes}
                          onChange={e => updateRow(idx, 'notes', e.target.value)} />
                      </td>
                      <td style={{ ...cellSt, textAlign: 'center' }}>
                        {formRows.length > 1 && (
                          <button onClick={() => removeRow(idx)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c5221f', fontWeight: 900, fontSize: 16, lineHeight: 1, padding: '0 4px' }}>×</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Add row */}
            {!editId && (
              <button onClick={addRow}
                style={{ padding: '5px 16px', borderRadius: 6, border: '1px dashed #1a73e8', background: '#e8f0fe', color: '#1a73e8', cursor: 'pointer', fontWeight: 700, fontSize: 13, marginBottom: 14 }}>
                + Add Row
              </button>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={!isFormValid || saving}
                style={{ opacity: isFormValid ? 1 : 0.45, cursor: isFormValid ? 'pointer' : 'not-allowed' }}>
                {saving ? 'Saving…' : editId ? '💾 Update' : '➕ Log Usage'}
              </button>
              <button className="btn btn-secondary" onClick={() => { setTab('list'); setEditId(null); setErr(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Report ── */}
      {tab === 'report' && (
        <div className="card">
          <div className="card-header"><div className="card-title">📊 Usage Summary by Product {filterCrop && cropCode ? `— ${cropCode}` : ''}</div></div>
          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr><th>Product</th><th>Category</th><th style={{ textAlign: 'right' }}>Applications</th><th style={{ textAlign: 'right' }}>Total Qty Used</th><th>UOM</th></tr>
              </thead>
              <tbody>
                {summaryRows.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No usage entries yet</td></tr>}
                {summaryRows.map((r, i) => {
                  const cat = CAT_COLOR[r.category] || CAT_COLOR['OTHER'];
                  return (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{r.product_name}</td>
                      <td><span style={{ background: cat.bg, color: cat.color, borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{r.category || '—'}</span></td>
                      <td style={{ textAlign: 'right' }}>{r.count}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{r.total.toFixed(3)}</td>
                      <td>{r.uom}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
